import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import { authStore } from '../../../api/stores/globalStores';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

const WHITE = 'rgba(255, 255, 255, 1)';
const YELLOW = 'rgba(251, 191, 36, 1)';

const BG_IMAGE =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-160b1d0d-ab92-488b-907c-b70e12ccbb14.png?e=webp&nll=true&cX=191&cY=0&cW=1370&cH=1080';

const CHARACTER_RIGHT =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-b218afc8-c372-4055-91c6-d4e19a6bbfb3.png?w=1075&e=webp&nll=true&cX=633&cY=662&cW=611&cH=418';

const CHARACTER_LEFT =
  'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5769455/image-b218afc8-c372-4055-91c6-d4e19a6bbfb3.png?w=1070&e=webp&nll=true&cX=634&cY=336&cW=608&cH=744';

interface TeamMember {
  name: string;
  title: string;
  bio: string;
  bulletPoints: string[];
  image: string;
}

const TOP_ROW: TeamMember[] = [
  {
    name: 'ALEXANDER ST. JOHN',
    title: 'Founder & CEO',
    bio: "Technology entrepreneur and inventor who co-created Microsoft\u2019s DirectX Media Architecture and Direct3D API\u2014foundational to modern PC gaming. Founder of WildTangent, one of the world\u2019s largest online game publishers, and later Nyriad, advancing GPU-accelerated scientific computing.",
    bulletPoints: [
      "Co-creator of Microsoft\u2019s DirectX Media Architecture and Direct3D API, foundational technologies for modern PC gaming.",
      "Founder of WildTangent, which became one of the world\u2019s largest online game publishers.",
      'Founder of Nyriad, pioneering GPU-accelerated scientific computing.',
      'Technology entrepreneur, inventor, and gaming industry pioneer.',
      "Subject of the book 'Renegades of the Empire'.",
    ],
    image: '/images/profiles/Alex.jpg',
  },
  {
    name: 'BILL GOSSMAN',
    title: 'Founder & Acting CFO',
    bio: 'Entrepreneur, executive, and investor with leadership across digital media, healthcare, SaaS, and photonics. Lead director at nLight and board member at HealthTap; former venture partner at Mohr Davidow Ventures and founder of @mobile (acquired for $400M). Previously held engineering roles at Bell Labs, AT&T, and Northrop; holds 14 patents.',
    bulletPoints: [
      'Entrepreneur, executive, and investor across digital media, healthcare, SaaS, photonics, and AI.',
      'Founder of @mobile, acquired for $400M.',
      'Lead Director at nLight and board member at HealthTap.',
      'Former Venture Partner at Mohr Davidow Ventures.',
      'Holds 14 patents and previously held roles at Bell Labs, AT&T, and Northrop.',
    ],
    image: '/images/profiles/Bill.jpg',
  },
  {
    name: 'DAVE MADDEN',
    title: 'Chief Business Officer',
    bio: 'Media and gaming technology entrepreneur specializing in platform strategy, live-service monetization, advertising, and brand partnerships. Creator of the "watch-to-earn" sponsorship model used across streaming services and free-to-play games; former SVP of Global Brand Partnerships at Electronic Arts and founding chairman of the IAB Games Committee.',
    bulletPoints: [
      'Media and gaming technology entrepreneur specializing in platform strategy and monetization.',
      'Creator of the "rewarded" or "watch-to-earn" sponsorship model used across streaming and free-to-play games.',
      'Former SVP of Global Brand Partnerships at Electronic Arts.',
      'Leader in advertising, sponsorships, and live-service game economies.',
      'Founding Chairman of the IAB Games Committee.',
    ],
    image: '/images/profiles/Dave.jpg',
  },
];

const BOTTOM_ROW: TeamMember[] = [
  {
    name: 'WILLIAM "BING" GORDON',
    title: 'Board Member',
    bio: 'Video game executive and venture capitalist with decades in leadership with interactive entertainment. Former Chief Creative Officer at Electronic Arts, partner at Kleiner Perkins, and founding director of Audible. Board experience includes Amazon, Duolingo, Zynga, and Ngmoco.',
    bulletPoints: [
      "Former Chief Creative Officer at Electronic Arts, helping shape the company\u2019s global creative strategy.",
      'Venture capitalist and partner at Kleiner Perkins.',
      'Founding director of Audible.',
      'Board member at major technology companies including Amazon, Duolingo, Zynga, and Ngmoco.',
      'Longtime leader in the interactive entertainment and gaming industry.',
    ],
    image: '/images/profiles/Bing.jpg',
  },
  {
    name: 'JOHN SALTER',
    title: 'Board Member',
    bio: 'Co-Founder and Partner at The Raine Group, a global merchant bank focused on technology, media, and telecommunications. Veteran gaming investor and advisor with board roles including DraftKings, Games24x7, Huuuge, Midnite, and Jagex.',
    bulletPoints: [
      'Co-Founder and Partner at The Raine Group, a global merchant bank focused on technology, media, and telecommunications.',
      'Veteran investor and strategic advisor in the global gaming industry.',
      'Board member at DraftKings, Games24x7, Huuuge, Midnite, and Jagex.',
      'Extensive experience advising and investing in leading gaming companies worldwide.',
    ],
    image: '/images/profiles/John.jpg',
  },
];

const BIO_LINE_HEIGHT = 14;
const BIO_COLLAPSED_LINES = 4;
const BIO_COLLAPSED_HEIGHT = BIO_LINE_HEIGHT * BIO_COLLAPSED_LINES;
const TRANSITION_SPEED = '0.35s';

const ProfileCard = ({ name, title, bio, image, cardWidth = '160px', isLarge = false }: TeamMember & { cardWidth?: string; isLarge?: boolean }) => {
  const [expanded, setExpanded] = useState(false);
  const bioRef = useRef<HTMLParagraphElement>(null);
  const [fullHeight, setFullHeight] = useState(0);

  useEffect(() => {
    if (bioRef.current) {
      setFullHeight(bioRef.current.scrollHeight);
    }
  }, [bio]);

  const needsTruncation = fullHeight > BIO_COLLAPSED_HEIGHT + 2;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      width: cardWidth,
    }}>
      <img
        src={image}
        alt={name}
        style={{
          width: isLarge ? '140px' : '101px',
          height: isLarge ? '140px' : '100px',
          objectFit: 'cover',
          borderRadius: '200px',
          border: `5px solid ${YELLOW}`,
        }}
      />
      <p style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 900,
        fontSize: isLarge ? '16px' : '12px',
        lineHeight: isLarge ? '20px' : '16px',
        color: YELLOW,
        textAlign: 'center',
        margin: '8px 0 0 0',
        fontVariationSettings: "'wght' 900",
      }}>
        {name}
      </p>
      <p style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 700,
        fontSize: isLarge ? '16px' : '12px',
        lineHeight: isLarge ? '18px' : '15px',
        color: WHITE,
        textAlign: 'center',
        margin: 0,
      }}>
        {title}
      </p>
      <p
        ref={bioRef}
        style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 300,
          fontSize: isLarge ? '16px' : '10px',
          lineHeight: isLarge ? '19px' : `${BIO_LINE_HEIGHT}px`,
          color: WHITE,
          textAlign: 'center',
          margin: '4px 0 0 0',
          overflow: 'hidden',
          maxHeight: expanded ? `${fullHeight}px` : `${BIO_COLLAPSED_HEIGHT}px`,
          transition: `max-height ${TRANSITION_SPEED} ease`,
          ...(expanded ? {} : {
            display: '-webkit-box',
            WebkitLineClamp: BIO_COLLAPSED_LINES,
            WebkitBoxOrient: 'vertical' as const,
          }),
        }}
      >
        {bio}
      </p>
      {needsTruncation && (
        <button
          onClick={() => { sendAnalytics({ name: 'Marketing Button Clicked', role: 'host', persistentId: getPersistentPlayerId(), playerAuthId: authStore.currentState.playerAuthId, page: window.location.pathname, buttonLabel: expanded ? `Show Less: ${name}` : `Read More: ${name}`, buttonLocation: 'MeetTheTeam' }); setExpanded(!expanded); }}
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '9px',
            fontWeight: 700,
            color: WHITE,
            background: 'transparent',
            border: `1px solid ${WHITE}`,
            borderRadius: '30px',
            padding: '3px 12px',
            cursor: 'pointer',
            marginTop: '4px',
            userSelect: 'none',
          }}
        >
          {expanded ? 'Show Less' : 'Read More'}
        </button>
      )}
    </div>
  );
};

export const MeetTheTeam = () => {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(desktopRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  return (
    <>
      {/* Desktop Layout */}
      <motion.section
        ref={desktopRef}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Background Image — absolute, fills container */}
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          overflow: 'hidden',
        }}>
          <img
            src={BG_IMAGE}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'rotate(180deg)',
              opacity: 0.85,
            }}
          />
        </div>

        {/* Decorative Character — Bottom Left */}
        <img
          src={CHARACTER_LEFT}
          alt=""
          style={{
            position: 'absolute',
            left: '1px',
            bottom: '60px',
            width: '214px',
            height: '262px',
            transform: 'rotate(155deg)',
            opacity: 1,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />

        {/* Decorative Character — Bottom Right */}
        <img
          src={CHARACTER_RIGHT}
          alt=""
          style={{
            position: 'absolute',
            right: '-40px',
            bottom: '40px',
            width: '215px',
            height: '147px',
            transform: 'rotate(70deg)',
            opacity: 1,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />

        {/* Content — in flow, drives section height */}
        <motion.div
          style={{
            position: 'relative',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '87px 0 80px',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* Title: Meet the Team. */}
          <h1 style={{
            lineHeight: '50px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: '50px',
            color: WHITE,
            margin: 0,
            textAlign: 'center',
            fontVariationSettings: "'wght' 900",
          }}>
            Meet the <span style={{ color: YELLOW }}>Team.</span>
          </h1>

          {/* Top Row — 3 profiles */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            marginTop: '30px',
          }}>
            {TOP_ROW.map((member, i) => (
              <ProfileCard key={i} {...member} />
            ))}
          </div>

          {/* Bottom Row — 2 profiles, centered */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            marginTop: '26px',
          }}>
            {BOTTOM_ROW.map((member, i) => (
              <ProfileCard key={i} {...member} />
            ))}
          </div>
        </motion.div>
      </motion.section>

      {/* Mobile Layout */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          backgroundColor: 'transparent',
          padding: '40px 16px',
          overflow: 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Background Image — rotated 180deg */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          transform: 'rotate(180deg)',
          opacity: 0.85,
          zIndex: 1,
        }} />

        {/* Content */}
        <div style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          width: '100%',
          maxWidth: '320px',
        }}>
          {/* Title */}
          <h1 style={{
            lineHeight: '42px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: '36px',
            color: WHITE,
            margin: 0,
            textAlign: 'center',
            fontVariationSettings: "'wght' 900",
          }}>
            Meet the <span style={{ color: YELLOW }}>Team.</span>
          </h1>

          {/* Profile List — single column on mobile */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '32px',
            width: '100%',
          }}>
            {[...TOP_ROW, ...BOTTOM_ROW].map((member, i) => (
              <ProfileCard key={i} {...member} cardWidth="300px" isLarge />
            ))}
          </div>
        </div>
      </motion.section>
    </>
  );
};
